import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';

import useWorkspaceId from '@/hooks/domain/useWorkspaceId';

import {
  sharedLinkByTargetQueryOptions,
  useCreateSharedLinkMutation,
} from '../api';
import type { SharedLink, SharedLinkTargetType } from '../api/types';

type Props = {
  children: (sharedLinkData: SharedLink) => ReactNode;
  targetId: string;
  targetType: SharedLinkTargetType;
  link?: string;
};

export function SharedLinkApiBody({
  children,
  targetId,
  targetType,
  link = `${window.location.origin}${window.location.pathname}`,
}: Props) {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const queryOptions = sharedLinkByTargetQueryOptions({
    workspaceId,
    targetId,
    targetType,
  });
  const { data } = useSuspenseQuery(queryOptions);
  const createMutation = useCreateSharedLinkMutation();

  useEffect(() => {
    if (data) {
      return;
    }

    createMutation.mutate(
      {
        workspaceId,
        targetId,
        targetType,
        link,
        expire: 0,
      },
      {
        onSuccess: (createdSharedLink) => {
          queryClient.setQueryData(queryOptions.queryKey, createdSharedLink);
        },
      },
    );
  }, [
    createMutation,
    data,
    link,
    queryClient,
    queryOptions.queryKey,
    targetId,
    targetType,
    workspaceId,
  ]);

  if (!data) {
    return null;
  }

  return <>{children(data)}</>;
}
